package dev.youndie.site.pages.blog

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initSmtpFromTheSpecPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("blog/SmtpFromTheSpec.md", FrontMatterElement.Builder { addScalar("title", "Writing SMTP from the RFC instead of around it"); addScalar("searchTitle", "SMTP client for Kotlin/Native — RFC 5321, STARTTLS and SASL"); addScalar("date", "2026-03-01"); addScalar("description", "A Kotlin/Native service has two ways to send mail — shell out to sendmail, or wrap libcurl. Both hide the protocol exactly where it has to be visible."); addScalar("library", "smtpkn"); }))
}

@Page("/blog/smtp-from-the-spec")
@Layout(".components.layouts.MarkdownLayout")
@Composable
fun SmtpFromTheSpecPage() {
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("On the JVM this was solved twenty years ago — Jakarta Mail, Simple Java Mail, done. On Kotlin/Native there is nothing, and the two workarounds both fail in the same way: shelling out to ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("sendmail")
        }
        org.jetbrains.compose.web.dom.Text(" and wrapping libcurl each hide the protocol at precisely the points where you need to see it.")
    }
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/smtpkn") {
            org.jetbrains.compose.web.dom.Text("smtpkn")
        }
        org.jetbrains.compose.web.dom.Text(" implements RFC 5321 over sockets instead. Here are the three places where reading the specification changed the design.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("replies-are-matched-by-counting") }) {
        org.jetbrains.compose.web.dom.Text("Replies are matched by counting")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The obvious way to match a reply to a command is by looking at it — the code, or the text. The specification forbids this, and it is right to.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Codes repeat. Text is server-defined and changes between releases. The only thing that holds is ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("order")
        }
        org.jetbrains.compose.web.dom.Text(": replies come back in the order the commands went out, one per command, and pipelining does not change that. So the parser counts.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("This sounds pedantic until ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("PIPELINING")
        }
        org.jetbrains.compose.web.dom.Text(" is negotiated and five commands are in flight. A matcher that reads codes will pair the wrong reply with the wrong command exactly when the session is busiest, and it will do it silently.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The parser also has to stream: multiline replies, enhanced status codes, arbitrary text. There is no reading until a blank line.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("a-partial-refusal-is-a-result-not-an-exception") }) {
        org.jetbrains.compose.web.dom.Text("A partial refusal is a result, not an exception")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Send to five recipients. Three are accepted, two are rejected. What does the function return?")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("If it throws, the caller has lost which three succeeded. And now the only safe options are to give up or to retry all five — the second of which delivers three duplicates.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("So a partial refusal is a value. The result carries who was accepted and who was not, and the caller decides. That distinction — between a retry and a duplicate — is the entire reason the type is shaped that way, and it is invisible until the first time it costs you.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("refuse-don-t-work-around") }) {
        org.jetbrains.compose.web.dom.Text("Refuse, don't work around")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A line break inside an address is not a formatting problem. It is a way to append headers of somebody else's choosing to your message. Same for a subject that carries a newline.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Every one of those is rejected rather than sanitised, along with credentials over a cleartext channel. Sanitising means guessing what the caller meant; refusing means they find out at the point they made the mistake.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-rest-of-it") }) {
        org.jetbrains.compose.web.dom.Text("The rest of it")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Submission per RFC 6409: port 587 with ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("STARTTLS")
            }
            org.jetbrains.compose.web.dom.Text(", port 465 with implicit TLS.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("TLS with the certificate chain ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("and the host name")
            }
            org.jetbrains.compose.web.dom.Text(" actually verified — OpenSSL on Kotlin/Native, ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("SSLEngine")
            }
            org.jetbrains.compose.web.dom.Text(" on the JVM.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Seven SASL mechanisms: ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("PLAIN")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("LOGIN")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("CRAM-MD5")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("SCRAM-SHA-1")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("SCRAM-SHA-256")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("XOAUTH2")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("OAUTHBEARER")
            }
            org.jetbrains.compose.web.dom.Text(".")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("ESMTP: ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("PIPELINING")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("SIZE")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("8BITMIME")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("SMTPUTF8")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("DSN")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("ENHANCEDSTATUSCODES")
            }
            org.jetbrains.compose.web.dom.Text(", ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("CHUNKING")
            }
            org.jetbrains.compose.web.dom.Text(", plus punycode for internationalised domains.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Message building: RFC 5322 headers, ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("multipart/alternative")
            }
            org.jetbrains.compose.web.dom.Text(", attachments, encoded words.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Timeouts whose defaults are the RFC minimums rather than numbers I liked.")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("one-platform-claimed-honestly") }) {
        org.jetbrains.compose.web.dom.Text("One platform, claimed honestly")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("linuxX64")
            }
            org.jetbrains.compose.web.dom.Text(" is the platform this is built for and the only one it is claimed to work on.")
        }
        org.jetbrains.compose.web.dom.Text(" That is where every milestone closes, where the whole suite runs, and where TLS is exercised against a real server and end to end against Mailpit and Postfix.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Other targets compile. They are not claimed, because \"it compiles\" and \"it delivers mail through a real MTA\" are different statements and only one of them is worth putting in a README.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Source: ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/smtpkn") {
            org.jetbrains.compose.web.dom.Text("github.com/youndie/smtpkn")
        }
    }
}
